import { Actions, type State } from 'Flux/Slice/index.ts'
import type { SinkId, SourceId } from 'Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import { isLoadingState } from 'Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ScatterChartNext, type ScatterPoint } from 'View/Component/Charts/ScatterChartNext.tsx'
import { TaxonNames, Taxons } from 'Types/Taxons.ts'
import type { MetadataFieldDescriptor } from 'View/Component/SampleGrouping/MetadataFields.ts'
import { generateMetadataFields } from 'View/Component/SampleGrouping/MetadataFields.ts'
import type { SampleBucket } from 'View/Component/SampleGrouping/OperationTypes.ts'
import { pcoa } from 'View/Component/Charts/math/pcoa.ts'
import { visualizationSlice } from 'Flux/Slice/Visualization/VisualizationSlice.ts'
import { selectCubeContext } from './Selector/CubeContext.ts'
import { selectFilteredCube } from './Selector/SelectFilteredCube.ts'

function brayCurtis(a: Record<string, number>, b: Record<string, number>, allKeys: Set<string>): number {
  let sumMin = 0
  let sumTotal = 0

  for (const k of allKeys) {
    sumMin += Math.min(a[k] ?? 0, b[k] ?? 0)
    sumTotal += (a[k] ?? 0) + (b[k] ?? 0)
  }

  return 1 - (2 * sumMin) / sumTotal
}

const GROUP_COLOR_PALETTE = ['#4C78A8', '#F58518', '#54A24B', '#EECA3B', '#B279A2', '#FF9DA6', '#9C755F', '#9AC8EB']

type BucketGrouping = { key: string; label: string }

function deriveBucketGrouping(bucket: SampleBucket | undefined, field?: MetadataFieldDescriptor): BucketGrouping {
  if (!bucket || !field) return { key: 'group:all', label: 'All samples' }

  for (const sample of bucket.samples) {
    const uiValue = field.uiLabel?.(sample)
    const rawValue = uiValue ?? field.getter(sample)

    if (rawValue !== undefined && rawValue !== null && rawValue !== '') {
      const comparison = field.comparisonKey?.(sample)
      const key = comparison ?? (typeof rawValue === 'string' || typeof rawValue === 'number' ? String(rawValue) : JSON.stringify(rawValue))
      return {
        key: key || 'group:unset',
        label: uiValue ?? String(rawValue)
      }
    }
  }

  return { key: 'group:unset', label: 'Unset' }
}

export function BetaDiversitySink({ sourceId, sinkId }: { sourceId: SourceId; sinkId: SinkId }) {
  const cubeCtx = useSelector((s: typeof State) => selectCubeContext(s, sourceId))
  const shared = useSelector((s: typeof State) => s.visualization.sources[sourceId]?.shared)
  const status = useSelector((s: typeof State) => s.visualization.sources[sourceId]?.status)
  const sinkConfig = useSelector((s: typeof State) => s.visualization.sources[sourceId]?.sinks[sinkId]?.config)
  const dispatch = useDispatch()
  const path = shared.selectedPath ?? []

  const groupFieldKey = (sinkConfig?.params?.groupFieldKey as string) ?? ''
  const level = (sinkConfig?.params?.level as Taxons) ?? Taxons.species

  const selectedBuckets = cubeCtx.status === 'ready' ? cubeCtx.selectedBuckets : []
  const selectedSamples = useMemo(() => selectedBuckets.flatMap((bucket) => bucket.samples), [selectedBuckets])
  const metadataFields = useMemo(() => generateMetadataFields(selectedSamples), [selectedSamples])

  const availableMetadataFields = useMemo(() => {
    const entries = Object.values(metadataFields)
    if (!selectedSamples.length) return []
    return entries
      .filter((field) =>
        selectedSamples.some((sample) => {
          const uiValue = field.uiLabel?.(sample)
          const rawValue = field.getter(sample)
          return (uiValue ?? rawValue ?? '') !== ''
        })
      )
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [metadataFields, selectedSamples])

  const selectedMetadataField = groupFieldKey && metadataFields[groupFieldKey] ? metadataFields[groupFieldKey] : undefined

  // Path + grade filter feeds an already-bucket-aggregated `{ taxa, magnitude, grade }[]` shape
  // per bucket. Shared, memoised across every sink on this source (see SelectFilteredCube).
  const filteredKronaData = useSelector((s: typeof State) =>
    selectFilteredCube(s, sourceId, { selectedPath: path, gradeThreshold: shared?.gradeThreshold ?? 0 })
  )

  const agg = useMemo(() => {
    return filteredKronaData.map((bucket) => {
      const aggregation: Record<string, number> = {}
      bucket.values.forEach((v) => {
        const nameAtLevel = v.taxa[level] ?? '(n/a)'
        aggregation[nameAtLevel] = (aggregation[nameAtLevel] ?? 0) + (v.magnitude || 0)
      })
      return { name: bucket.bucketLabel, aggregation }
    })
  }, [filteredKronaData, level])

  const allNames = useMemo(() => {
    const names = new Set<string>()
    filteredKronaData.forEach((bucket) => {
      bucket.values.forEach((v) => {
        const nameAtLevel = v.taxa[level] ?? '(n/a)'
        names.add(nameAtLevel)
      })
    })
    return names
  }, [filteredKronaData, level])

  const distMatrix: number[][] = []
  for (let r = 0; r < agg.length; r++) {
    distMatrix[r] = []
    for (let r2 = 0; r2 < agg.length; r2++) {
      distMatrix[r][r2] = brayCurtis(agg[r].aggregation, agg[r2].aggregation, allNames)
    }
  }

  const pcoaCoords = distMatrix.length ? pcoa(distMatrix) : []

  if (cubeCtx.status !== 'ready') {
    return (
      <div className="text-sm opacity-70">
        Waiting for data… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}
      </div>
    )
  }
  const data = pcoaCoords.map((coord, index) => {
    const bucket = selectedBuckets[index]
    const grouping = deriveBucketGrouping(bucket, selectedMetadataField)
    return {
      x: coord[0],
      y: coord[1],
      name: bucket?.label || '',
      taxaData: agg[index] || '',
      groupKey: grouping.key,
      groupLabel: grouping.label
    }
  })

  const groupOrder = useMemo(() => Array.from(new Set(data.map((item) => item.groupKey))), [data])

  const colorByGroupKey = useMemo(() => {
    const map: Record<string, string> = {}
    groupOrder.forEach((key, index) => {
      map[key] = GROUP_COLOR_PALETTE[index % GROUP_COLOR_PALETTE.length]
    })
    return map
  }, [groupOrder])

  const groupLabelByKey = useMemo(() => {
    const map: Record<string, string> = {}
    data.forEach((item) => {
      if (!map[item.groupKey]) {
        map[item.groupKey] = item.groupLabel
      }
    })
    return map
  }, [data])

  const handleGroupFieldKeyChange = (value: string) => {
    dispatch(
      Actions[visualizationSlice.name].registerSink({
        sourceId: sourceId,
        sinkconfig: { ...sinkConfig, params: { ...sinkConfig?.params, groupFieldKey: value } }
      })
    )
  }

  const handleLevelChange = (value: Taxons) => {
    dispatch(
      Actions[visualizationSlice.name].registerSink({
        sourceId: sourceId,
        sinkconfig: { ...sinkConfig, params: { ...sinkConfig?.params, level: value } }
      })
    )
  }

  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="text-xs opacity-70">Scatterchart: PCoA (Bray-Curtis)</div>

      <details className=" flex flex-col gap-2 animate-fade-in-down m-2">
        <summary className="text-xs opacity-70 cursor-pointer">Options</summary>
        <div className="flex items-center gap-2 justify-between">
          <label>Select taxonomy depth</label>
          <select
            className="select select-sm select-bordered"
            value={level}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10)
              handleLevelChange(v)
            }}
          >
            {[Taxons.phylum, Taxons.class, Taxons.order, Taxons.family, Taxons.genus, Taxons.species].map((lvl, index) => (
              <option key={index} value={lvl}>
                {TaxonNames[lvl]}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 justify-between">
          <label>Color by metadata</label>
          <select
            className="select select-sm select-bordered"
            value={groupFieldKey}
            onChange={(e) => handleGroupFieldKeyChange(e.target.value)}
            //.setGroupFieldKey({ sourceId, sinkId, groupFieldKey: e.target.value }))} setGroupFieldKey(e.target.value)}
          >
            <option value="">None</option>
            {availableMetadataFields.map((field) => (
              <option key={field.key} value={field.key}>
                {field.label}
              </option>
            ))}
          </select>
        </div>
      </details>
      {selectedMetadataField && groupOrder.length > 0 && (
        <div className="flex flex-wrap gap-3 text-xs">
          {groupOrder.map((key) => (
            <div key={key} className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: colorByGroupKey[key] }} />
              <span>{groupLabelByKey[key]}</span>
            </div>
          ))}
        </div>
      )}
      {data.length === 0 && <div className="text-sm opacity-70">No data for current selection.</div>}
      {isLoadingState(status) && <div className="text-sm"> Loading...</div>}
      {status === 'dataReady' && (
        <ScatterChartNext
          xName="PC1"
          yName="PC2"
          height={'100%'}
          data={data.map(
            (entry): ScatterPoint => ({
              x: entry.x,
              y: entry.y,
              name: entry.name,
              color: colorByGroupKey[entry.groupKey],
              group: selectedMetadataField ? entry.groupLabel : undefined,
              details: Object.entries((entry.taxaData as { aggregation?: Record<string, number> })?.aggregation ?? {})
            })
          )}
        />
      )}
    </div>
  )
}
